import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  UserIcon,
  CalendarDaysIcon,
  PhoneIcon,
  EnvelopeIcon,
  ShieldCheckIcon,
  BuildingOfficeIcon,
  BriefcaseIcon,
  HandThumbUpIcon,
  HandThumbDownIcon,
} from "@heroicons/react/24/outline";
import { rsvpService } from "../services/rsvpService";
import { RSVPFormData } from "../types";
import { EVENT_DATA } from "../constants/eventData";
import logo from "../assets/logo.png";

const RSVPPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [existingRSVP] = useState<any>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<Partial<RSVPFormData>>({});

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<RSVPFormData>({
    defaultValues: {
      attendance: "attending",
      company: "",
      role: "",
    },
  });

  // Step navigation functions
  const nextStep = (data: Partial<RSVPFormData>) => {
    const updatedFormData = { ...formData, ...data };
    setFormData(updatedFormData);

    if (currentStep === 1) {
      // After attendance selection, go to personal info
      setCurrentStep(2);
    } else if (currentStep === 2) {
      // After personal info, go to additional info
      setCurrentStep(3);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Use hardcoded event data instead of API call
  const event = EVENT_DATA;

  // Submit RSVP mutation (no eventId needed)
  const submitRSVPMutation = useMutation({
    mutationFn: (data: RSVPFormData) => rsvpService.submitRSVP(data),
    onSuccess: (data) => {
      setIsSubmitted(true);
      queryClient.invalidateQueries({ queryKey: ["event"] });
    },
    onError: (error) => {
      // Error handling is done in the UI
    },
  });

  // Update RSVP mutation (no eventId needed)
  const updateRSVPMutation = useMutation({
    mutationFn: (data: RSVPFormData & { email: string }) =>
      rsvpService.updateRSVP(data),
    onSuccess: (data) => {
      setIsSubmitted(true);
      queryClient.invalidateQueries({ queryKey: ["event"] });
    },
    onError: (error) => {
      // Error handling is done in the UI
    },
  });

  const onSubmit = (data: RSVPFormData) => {
    if (existingRSVP) {
      updateRSVPMutation.mutate({ ...data, email: existingRSVP.email });
    } else {
      submitRSVPMutation.mutate(data);
    }
  };

  const handleStepSubmit = (stepData: Partial<RSVPFormData>) => {
    if (currentStep === 3) {
      // Final step - submit the form
      const finalData = { ...formData, ...stepData } as RSVPFormData;
      onSubmit(finalData);
    } else {
      // Move to next step
      nextStep(stepData);
    }
  };

  // No loading or error handling needed since we're using hardcoded data

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-black text-white overflow-hidden">
        {/* Animated Background */}
        <div className="fixed inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-700 via-primary-600 to-primary-800" />
          <motion.div className="absolute inset-0 opacity-20">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse" />
            <div className="absolute top-3/4 right-1/4 w-96 h-96 bg-primary-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse" />
          </motion.div>
          <motion.div className="absolute inset-0 opacity-10">
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-primary-400 rounded-full mix-blend-multiply filter blur-xl animate-pulse" />
          </motion.div>
        </div>

        <div className="relative z-10 min-h-screen flex items-center justify-center">
          <motion.div
            className="text-center max-w-md mx-auto px-6"
            initial={{ opacity: 0, scale: 0.7, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{
              duration: 0.8,
              ease: [0.25, 0.46, 0.45, 0.94],
              delay: 0.2,
            }}
          >
            <motion.div
              className="bg-white/15 backdrop-blur-md border border-white/20 rounded-3xl p-8 shadow-2xl"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.6,
                delay: 0.4,
                ease: "easeOut",
              }}
            >
              <motion.div
                className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${
                  formData.attendance === "attending"
                    ? "bg-success-500/30"
                    : "bg-danger-500/30"
                }`}
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{
                  duration: 0.8,
                  delay: 0.6,
                  ease: "backOut",
                }}
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{
                    duration: 0.4,
                    delay: 0.8,
                    ease: "backOut",
                  }}
                >
                  <CheckCircleIcon
                    className={`w-10 h-10 ${
                      formData.attendance === "attending"
                        ? "text-success-300"
                        : "text-danger-300"
                    }`}
                  />
                </motion.div>
              </motion.div>

              <motion.h1
                className="text-3xl font-bold text-white mb-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.6,
                  delay: 0.8,
                  ease: "easeOut",
                }}
              >
                {formData.attendance === "attending"
                  ? "RSVP Submitted!"
                  : "We'll Miss You!"}
              </motion.h1>

              <motion.p
                className="text-gray-200 mb-8 text-lg leading-relaxed"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.6,
                  delay: 1.0,
                  ease: "easeOut",
                }}
              >
                {formData.attendance === "attending"
                  ? "Thank you for your response. We look forward to seeing you at the event!"
                  : "Sorry you can't make it. We will miss you!"}
              </motion.p>

              <motion.button
                onClick={() => navigate("/")}
                className="inline-flex items-center px-8 py-4 text-white font-semibold rounded-xl transition-all duration-300 shadow-2xl hover:scale-105"
                style={{ backgroundColor: "#1434CB" }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.6,
                  delay: 1.2,
                  ease: "easeOut",
                }}
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                <ArrowLeftIcon className="w-5 h-5 mr-2" />
                Back to Home
              </motion.button>
            </motion.div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-700 via-primary-600 to-primary-800" />
        <motion.div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse" />
          <div className="absolute top-3/4 right-1/4 w-96 h-96 bg-primary-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse" />
        </motion.div>
        <motion.div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-primary-400 rounded-full mix-blend-multiply filter blur-xl animate-pulse" />
        </motion.div>
      </div>

      {/* Navigation */}
      <motion.nav
        className="relative z-50 bg-primary-600/10 backdrop-blur-md border-b border-white/10"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <motion.button
              onClick={() => navigate("/")}
              className="flex items-center"
              whileHover={{ scale: 1.05 }}
            >
              <img
                src={logo}
                alt="Visa Logo"
                className="w-24 h-24 object-contain"
              />
            </motion.button>
          </div>
        </div>
      </motion.nav>

      {/* Main Content */}
      <main className="relative z-10 pt-16 sm:pt-20 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-6 sm:p-8 lg:p-12"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <motion.div
              className="text-center mb-8 sm:mb-12"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
                {event.title}
              </h3>
              <p className="text-white text-lg sm:text-xl max-w-2xl mx-auto">
                You are cordially invited to connect and engage at Visa Connect
                Ethiopia 2025.
              </p>
            </motion.div>

            {/* Step-by-Step Form */}
            <div className="space-y-8">
              {/* Progress Indicator */}
              <motion.div
                className="flex items-center justify-center space-x-4 mb-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                {[1, 2, 3].map((step) => (
                  <div key={step} className="flex items-center">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${
                        step === currentStep
                          ? "bg-primary-600 text-white border-2 border-white shadow-lg scale-110"
                          : step < currentStep
                          ? "bg-white text-primary-600 border-2 border-white"
                          : "bg-white/20 text-white/60 border-2 border-white/30"
                      }`}
                    >
                      {step}
                    </div>
                    {step < 3 && (
                      <div
                        className={`w-8 h-1 mx-2 transition-all duration-300 rounded-full ${
                          step < currentStep ? "bg-white" : "bg-white/20"
                        }`}
                      />
                    )}
                  </div>
                ))}
              </motion.div>

              <AnimatePresence mode="wait">
                {/* Step 1: Attendance Selection */}
                {currentStep === 1 && (
                  <motion.div
                    key="step1"
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -50 }}
                    transition={{ duration: 0.5 }}
                    className="space-y-6"
                  >
                    <div className="text-center mb-8">
                      <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                        <CalendarDaysIcon className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-2xl font-bold text-white mb-2">
                        Will you be attending?
                      </h3>
                      <p className="text-white">
                        Let us know if you can join us for this special event
                      </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <motion.button
                        onClick={() => {
                          setValue("attendance", "attending");
                          handleStepSubmit({ attendance: "attending" });
                        }}
                        className={`group relative flex items-center p-6 border-2 rounded-2xl cursor-pointer transition-all duration-300 ${
                          formData.attendance === "attending"
                            ? "border-success-400 bg-success-500/30 shadow-lg shadow-success-500/20"
                            : "border-success-500/50 bg-success-500/10 hover:bg-success-500/20 hover:border-success-400"
                        }`}
                        whileHover={{
                          scale: 1.02,
                          transition: { duration: 0.2 },
                        }}
                        whileTap={{
                          scale: 0.95,
                          transition: { duration: 0.1 },
                        }}
                        animate={{
                          boxShadow:
                            formData.attendance === "attending"
                              ? "0 10px 25px -5px rgba(34, 197, 94, 0.3)"
                              : "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                        }}
                      >
                        <div className="flex items-center space-x-4">
                          <motion.div
                            className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 ${
                              formData.attendance === "attending"
                                ? "bg-success-400 shadow-lg"
                                : "bg-success-500"
                            }`}
                            animate={{
                              scale:
                                formData.attendance === "attending" ? 1.1 : 1,
                              rotate:
                                formData.attendance === "attending" ? 5 : 0,
                            }}
                          >
                            <HandThumbUpIcon className="w-6 h-6 text-white" />
                          </motion.div>
                          <div className="text-left">
                            <motion.p
                              className={`font-semibold text-lg transition-colors duration-300 ${
                                formData.attendance === "attending"
                                  ? "text-success-200"
                                  : "text-white"
                              }`}
                              animate={{
                                x: formData.attendance === "attending" ? 2 : 0,
                              }}
                            >
                              Yes, I'll be there!
                            </motion.p>
                            <p className="text-white">
                              I look forward to attending
                            </p>
                          </div>
                        </div>
                        {formData.attendance === "attending" && (
                          <motion.div
                            className="absolute top-2 right-2 w-6 h-6 bg-success-400 rounded-full flex items-center justify-center"
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.3, delay: 0.1 }}
                          >
                            <CheckCircleIcon className="w-4 h-4 text-white" />
                          </motion.div>
                        )}
                      </motion.button>

                      <motion.button
                        onClick={() => {
                          setValue("attendance", "not-attending");
                          handleStepSubmit({ attendance: "not-attending" });
                        }}
                        className={`group relative flex items-center p-6 border-2 rounded-2xl cursor-pointer transition-all duration-300 ${
                          formData.attendance === "not-attending"
                            ? "border-danger-400 bg-danger-500/30 shadow-lg shadow-danger-500/20"
                            : "border-danger-500/50 bg-danger-500/10 hover:bg-danger-500/20 hover:border-danger-400"
                        }`}
                        whileHover={{
                          scale: 1.02,
                          transition: { duration: 0.2 },
                        }}
                        whileTap={{
                          scale: 0.95,
                          transition: { duration: 0.1 },
                        }}
                        animate={{
                          boxShadow:
                            formData.attendance === "not-attending"
                              ? "0 10px 25px -5px rgba(239, 68, 68, 0.3)"
                              : "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                        }}
                      >
                        <div className="flex items-center space-x-4">
                          <motion.div
                            className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 ${
                              formData.attendance === "not-attending"
                                ? "bg-danger-400 shadow-lg"
                                : "bg-danger-500"
                            }`}
                            animate={{
                              scale:
                                formData.attendance === "not-attending"
                                  ? 1.1
                                  : 1,
                              rotate:
                                formData.attendance === "not-attending" ? 5 : 0,
                            }}
                          >
                            <HandThumbDownIcon className="w-6 h-6 text-white" />
                          </motion.div>
                          <div className="text-left">
                            <motion.p
                              className={`font-semibold text-lg transition-colors duration-300 ${
                                formData.attendance === "not-attending"
                                  ? "text-danger-200"
                                  : "text-white"
                              }`}
                              animate={{
                                x:
                                  formData.attendance === "not-attending"
                                    ? 2
                                    : 0,
                              }}
                            >
                              No, I can't make it
                            </motion.p>
                            <p className="text-white">
                              Unfortunately unavailable
                            </p>
                          </div>
                        </div>
                        {formData.attendance === "not-attending" && (
                          <motion.div
                            className="absolute top-2 right-2 w-6 h-6 bg-danger-400 rounded-full flex items-center justify-center"
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.3, delay: 0.1 }}
                          >
                            <CheckCircleIcon className="w-4 h-4 text-white" />
                          </motion.div>
                        )}
                      </motion.button>
                    </div>
                  </motion.div>
                )}

                {/* Step 2: Personal Information */}
                {currentStep === 2 && (
                  <motion.div
                    key="step2"
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -50 }}
                    transition={{ duration: 0.5 }}
                    className="space-y-6"
                  >
                    <div className="text-center mb-8">
                      <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                        <UserIcon className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-2xl font-bold text-white mb-2">
                        Personal Information
                      </h3>
                      <p className="text-white">
                        Please provide a brief introduction about yourself
                      </p>
                    </div>

                    <form
                      onSubmit={handleSubmit((data) => handleStepSubmit(data))}
                      className="space-y-4"
                    >
                      <div>
                        <label className="block text-sm font-medium text-white mb-3">
                          Full Name *
                        </label>
                        <div className="relative">
                          <UserIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white" />
                          <input
                            {...register("fullName", {
                              required: "Full name is required",
                            })}
                            type="text"
                            className="w-full pl-12 pr-4 py-4 bg-white/5 backdrop-blur-sm border border-white/20 rounded-2xl text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                            placeholder="Enter your full name"
                          />
                        </div>
                        {errors.fullName && (
                          <p className="text-red-400 text-sm mt-2">
                            {errors.fullName.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-white mb-3">
                          Email Address *
                        </label>
                        <div className="relative">
                          <EnvelopeIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white" />
                          <input
                            {...register("email", {
                              required: "Email is required",
                              pattern: {
                                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                message: "Please enter a valid email address",
                              },
                            })}
                            type="email"
                            className="w-full pl-12 pr-4 py-4 bg-white/5 backdrop-blur-sm border border-white/20 rounded-2xl text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                            placeholder="Enter your email"
                          />
                        </div>
                        {errors.email && (
                          <p className="text-red-400 text-sm mt-2">
                            {errors.email.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-white mb-3">
                          Phone Number *
                        </label>
                        <div className="relative">
                          <PhoneIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white" />
                          <input
                            {...register("phone", {
                              required: "Phone number is required",
                            })}
                            type="tel"
                            className="w-full pl-12 pr-4 py-4 bg-white/5 backdrop-blur-sm border border-white/20 rounded-2xl text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                            placeholder="Enter your phone number"
                          />
                        </div>
                        {errors.phone && (
                          <p className="text-red-400 text-sm mt-2">
                            {errors.phone.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-white mb-3">
                          Company *
                        </label>
                        <div className="relative">
                          <BuildingOfficeIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white" />
                          <input
                            {...register("company", {
                              required: "Company is required",
                            })}
                            type="text"
                            className="w-full pl-12 pr-4 py-4 bg-white/5 backdrop-blur-sm border border-white/20 rounded-2xl text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                            placeholder="Enter your company name"
                          />
                        </div>
                        {errors.company && (
                          <p className="text-red-400 text-sm mt-2">
                            {errors.company.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-white mb-3">
                          Role *
                        </label>
                        <div className="relative">
                          <BriefcaseIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white" />
                          <input
                            {...register("role", {
                              required: "Role is required",
                            })}
                            type="text"
                            className="w-full pl-12 pr-4 py-4 bg-white/5 backdrop-blur-sm border border-white/20 rounded-2xl text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                            placeholder="Enter your role at the company"
                          />
                        </div>
                        {errors.role && (
                          <p className="text-red-400 text-sm mt-2">
                            {errors.role.message}
                          </p>
                        )}
                      </div>

                      <div className="flex space-x-4 pt-4">
                        <button
                          type="button"
                          onClick={prevStep}
                          className="flex-1 px-6 py-3 bg-white/10 border border-white/20 text-white rounded-xl hover:bg-white/20 transition-all"
                        >
                          Back
                        </button>
                        <button
                          type="submit"
                          className="flex-1 px-6 py-3 text-white font-semibold rounded-xl transition-all"
                          style={{ backgroundColor: "#1434CB" }}
                        >
                          Continue
                        </button>
                      </div>
                    </form>
                  </motion.div>
                )}

                {/* Step 3: Additional Information */}
                {currentStep === 3 && (
                  <motion.div
                    key="step3"
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -50 }}
                    transition={{ duration: 0.5 }}
                    className="space-y-6"
                  >
                    <div className="text-center mb-8">
                      <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                        <ShieldCheckIcon className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-2xl font-bold text-white mb-2">
                        Additional Information
                      </h3>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-white mb-3">
                          Questions or Comments (Optional)
                        </label>
                        <textarea
                          {...register("specialRequests")}
                          rows={4}
                          className="w-full px-4 py-4 bg-white/5 backdrop-blur-sm border border-white/20 rounded-2xl text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all resize-none"
                        />
                      </div>

                      <div className="flex space-x-4 pt-4">
                        <motion.button
                          type="button"
                          onClick={prevStep}
                          className="flex-1 px-6 py-3 bg-white/10 border border-white/20 text-white rounded-xl hover:bg-white/20 transition-all duration-300"
                          whileHover={{
                            scale: 1.02,
                            backgroundColor: "rgba(255, 255, 255, 0.2)",
                          }}
                          whileTap={{
                            scale: 0.95,
                            transition: { duration: 0.1 },
                          }}
                        >
                          Back
                        </motion.button>
                        <motion.button
                          type="button"
                          onClick={() => {
                            // Get the current form values and merge with accumulated data
                            const currentValues = watch();
                            const finalData = {
                              ...formData,
                              ...currentValues,
                            } as RSVPFormData;

                            // Clean up empty strings for optional fields
                            if (finalData.specialRequests === "") {
                              finalData.specialRequests = undefined;
                            }
                            if (finalData.company === "") {
                              finalData.company = undefined;
                            }
                            if (finalData.role === "") {
                              finalData.role = undefined;
                            }
                            if (finalData.phone === "") {
                              finalData.phone = undefined;
                            }
                            // Validate required fields
                            if (
                              !finalData.fullName ||
                              !finalData.email ||
                              !finalData.attendance
                            ) {
                              // Show validation error in UI
                              return;
                            }

                            onSubmit(finalData);
                          }}
                          disabled={
                            submitRSVPMutation.isPending ||
                            updateRSVPMutation.isPending
                          }
                          className="flex-1 px-6 py-3 text-white font-semibold rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                          style={{ backgroundColor: "#1434CB" }}
                          whileHover={{
                            scale: 1.02,
                            boxShadow:
                              "0 10px 25px -5px rgba(59, 130, 246, 0.4)",
                          }}
                          whileTap={{
                            scale: 0.95,
                            transition: { duration: 0.1 },
                          }}
                        >
                          {submitRSVPMutation.isPending ||
                          updateRSVPMutation.isPending
                            ? "Submitting..."
                            : "Submit RSVP"}
                        </motion.button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default RSVPPage;
